import axios from "axios";
import { memoizedRefreshToken } from "./refreshToken";
import { clearAllCookies } from "./logout";
import { rememberReturnPath } from "./entryRouting";
import { isHubAccessDenied, redirectToHubAccessDenied, applyHubPortHeader } from "./hubAccess";

const baseUrl = process.env.NEXT_PUBLIC_BACKEND_PATH;
const axiosParent = () => {
    const axiosInstance = axios.create({
        baseUrl,
        withCredentials: true,
        headers: {
            "Content-Type": "application/json",
        },
    });

    // The port the dashboard shows travels as X-Omc-Port (SPEC-012 D11).
    axiosInstance.interceptors.request.use(applyHubPortHeader);

    // Add a response interceptor to handle 401 errors
    axiosInstance.interceptors.response.use(
        (response) => {
            return response;
        },
        async function (error) {
            const originalRequest = error.config;

            if (error.response) {
                // Wider Access capability denial: not an auth problem, so no
                // token refresh; surface it to the UI instead.
                if (error.response.status === 403 && error.response.data?.error === "capability_denied") {
                    if (typeof window !== "undefined") {
                        window.dispatchEvent(new CustomEvent("wider-access:denied", { detail: error.response.data }));
                    }
                    return Promise.reject(error);
                }

                // No Hub access (SPEC-012): not an auth problem either, so no
                // refresh and no retry; the Launch Pad explains and offers Sign out.
                if (isHubAccessDenied(error)) {
                    redirectToHubAccessDenied();
                    return Promise.reject(error);
                }

                // Disabled account: the session is over, no refresh, sign out now.
                if (error.response.status === 401 && error.response.data?.error === "account_disabled") {
                    if (typeof window !== "undefined" && window.location.pathname !== "/") {
                        clearAllCookies();
                        localStorage.clear();
                        window.location.href = "/";
                    }
                    return Promise.reject(error);
                }

                // Handle 401/403 errors by refreshing the cookie
                if ((error.response.status === 401 || error.response.status === 403) && !originalRequest._retry &&
                window.location.pathname !== '/') {
                    originalRequest._retry = true;

                    try {
                        // Refresh the cookie (no need to get a token back since it's in the cookie)
                        await memoizedRefreshToken();

                        // Retry the original request - the cookie will be sent automatically
                        // No need to modify headers since cookies are sent automatically
                        return axiosInstance(originalRequest);
                    } catch (refreshError) {
                        // If refresh fails, redirect to login — remembering the page
                        // so the sign-in brings the user straight back (SPEC-007).
                        console.log("Token refresh failed:", refreshError);
                        rememberReturnPath(window.location.pathname + window.location.search + window.location.hash);
                        localStorage.clear();
                        window.location.href = "/";
                        return Promise.reject(refreshError);
                    }
                }
            } else if (error.request) {
                // The request was made but no response was received
                console.log("The request was made but no response was received.", error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                console.log("Error when setting up the request", error.message);
            }

            return Promise.reject(error);
        },
    );

    return axiosInstance;
};

export default axiosParent;
